import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  StatsAlarmEditForm,
  StatsAlarmEditInitialValues,
  StatsAlarmComponents,
  StatsAlarmAdditionalParams,
  StatsAlarmFormOptions,
  onStatsAlarmFormSaved,
  IStatsAlarmFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedStatsAlarm, ISharedPatchStatsAlarm, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchStatsAlarm, patchStatsAlarm, getStatsAlarmUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useStatsCustomMetrics, useUsers, useQueues, usePauses, useDispositions, useTeams, useMailAccounts, useVoiceRoutes, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedStatsAlarm> {}

const StatsAlarmFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = true, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const stats_custom_metrics = useStatsCustomMetrics();
  const users = useUsers();
  const queues = useQueues();
  const pauses = usePauses();
  const dispositions = useDispositions();
  const teams = useTeams();
  const mail_accounts = useMailAccounts();
  const voice_routes = useVoiceRoutes();

  useEffect(() => {
    fetchStatsAlarm(id).then(stats_alarm => setValue(stats_alarm));
  }, [id, setValue, ]);

  const param_props: IStatsAlarmFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    stats_custom_metrics: stats_custom_metrics.stats_custom_metrics,
    users: users.users,
    queues: queues.queues,
    pauses: pauses.pauses,
    dispositions: dispositions.dispositions,
    teams: teams.teams,
    mail_accounts: mail_accounts.mail_accounts,
    voice_routes: voice_routes.voice_routes,
  };
  const formLayout: IForm | undefined = useMemo(() => value && StatsAlarmEditForm(value, param_props, t), [value, organizations, stats_custom_metrics, users, queues, pauses, dispositions, teams, mail_accounts, voice_routes, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && StatsAlarmFormOptions(value, param_props, t), [value, organizations, stats_custom_metrics, users, queues, pauses, dispositions, teams, mail_accounts, voice_routes, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchStatsAlarm, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchStatsAlarm) => value && save(() => patchStatsAlarm(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onStatsAlarmFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !stats_custom_metrics.loaded || !users.loaded || !queues.loaded || !pauses.loaded || !dispositions.loaded || !teams.loaded || !mail_accounts.loaded || !voice_routes.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.StatsAlarm, value)}
    >
      <AtlasForm<ISharedPatchStatsAlarm>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={StatsAlarmComponents}
        additionalParams={StatsAlarmAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={StatsAlarmEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('STATS_ALARM')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const StatsAlarmFormEditView = React.memo(withEdit(StatsAlarmFormEdit, getStatsAlarmUpdateSchema));
